import { Download, FileText } from "lucide-react";
import { BlockImage } from "./BlockImage";
import type { DocumentBlock } from "./types";
import type { TextStyle } from "./types";
import { inlineContent } from "./document-converter";

function StyledText({ text, styles }: { text: string; styles?: TextStyle[] }) {
  if (!styles?.length) return text;
  return inlineContent(text, styles).map((node, index) => {
    if (node.type === "hardBreak") return <br key={index} />;
    const href = node.marks?.find(mark => mark.type === "link")?.attrs?.href as string | undefined;
    const content = <span style={{
    fontWeight: node.marks?.some(mark => mark.type === "bold") ? 700 : undefined,
    fontStyle: node.marks?.some(mark => mark.type === "italic") ? "italic" : undefined,
    textDecoration: node.marks?.filter(mark => ["underline", "strike"].includes(mark.type)).map(mark => mark.type === "strike" ? "line-through" : "underline").join(" ") || undefined,
    fontFamily: node.marks?.some(mark => mark.type === "code") ? "monospace" : undefined,
    color: node.marks?.find(mark => mark.type === "textColor")?.attrs?.color,
    }}>{node.text}</span>;
    return href ? <a key={index} href={href} target="_blank" rel="noopener noreferrer">{content}</a> : <span key={index}>{content}</span>;
  });
}

export function BlockRenderer({ blocks, loadImage, onDownloadFile }: {
  blocks: DocumentBlock[];
  loadImage?: (fileId: string) => Promise<Blob>;
  onDownloadFile?: (fileId: string, name: string) => void;
}) {
  return <div className="block-document-view">
    {blocks.map((block) => {
      if (block.type === "PARAGRAPH") {
        const text = <StyledText text={block.text} styles={block.formatting?.text} />;
        if (block.style === "HEADING_1") return <h3 className="block-paragraph block-heading-1" key={block.id}>{text}</h3>;
        if (block.style === "HEADING_2") return <h4 className="block-paragraph block-heading-2" key={block.id}>{text}</h4>;
        if (block.style === "HEADING_3") return <h5 className="block-paragraph block-heading-3" key={block.id}>{text}</h5>;
        return <p className={`block-paragraph block-style-${(block.style || "BODY").toLowerCase()}`} key={block.id}>{text}</p>;
      }
      if (block.type === "TABLE") return <figure className="block-table" key={block.id}>
        {block.caption && <figcaption>{block.caption}</figcaption>}
        <div><table><thead><tr>{block.columns.map((column, index) => <th key={index}><StyledText text={column} styles={block.formatting?.[`0:${index}`]} /></th>)}</tr></thead>
          <tbody>{block.rows.map((row, rowIndex) => <tr key={rowIndex}>{block.columns.map((_, columnIndex) => <td key={columnIndex}><StyledText text={row[columnIndex] || "-"} styles={block.formatting?.[`${rowIndex + 1}:${columnIndex}`]} /></td>)}</tr>)}</tbody>
        </table></div>
      </figure>;
      if (block.type === "IMAGE") return <figure className="block-image" key={block.id}>
        <BlockImage block={block} loadImage={loadImage} />
        <figcaption><b>{block.caption || block.file.originalName}</b>{block.caption && <small>{block.file.originalName}</small>}</figcaption>
      </figure>;
      return <button type="button" className="block-file" key={block.id} onClick={() => onDownloadFile?.(block.file.id, block.file.originalName)}>
        <FileText size={18} /><span><b>{block.caption || block.file.originalName}</b><small>{block.file.originalName}</small></span><Download size={15} />
      </button>;
    })}
  </div>;
}
